import { useEffect, useState, type CSSProperties } from 'react'
import { useGiveawayCategory } from '@/hooks/queries'
import { useTicker } from '@/hooks/use-ticker'
import { GIVEAWAY_CATEGORIES, type GiveawayCategory } from '@/api/query-keys'
import { useOverlayStore } from '@/store/overlays'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { Giveaway } from '@/models/giveaway'
import { Avatar } from '@/components/ui/Avatar'
import { GiveawayFeed } from '@/components/giveaway/GiveawayFeed'
import { ValueBadge, ValueBadgeSkeleton } from '@/components/giveaway/ValueBadge'
import { SkeletonLine } from '@/components/ui/Skeleton'

const CATEGORY_LABELS: Record<GiveawayCategory, string> = {
  all: 'All',
  new: 'New',
  featured: 'Featured',
  trending: 'Trending',
  popular: 'Popular',
  ending: 'Ending',
}

const CATEGORY_ICONS: Record<GiveawayCategory, string> = {
  all: '/icons/stack.png',
  new: '/icons/new_icon.png',
  featured: '/icons/featured.png',
  trending: '/icons/trending.png',
  popular: '/icons/trending.png',
  ending: '/icons/ending.png',
}

/** How long the featured carousel rests on each entry before advancing. */
const AUTOPLAY_MS = 9000

/** Port of pages/Home.vue */
export function Home() {
  const [category, setCategory] = useState<GiveawayCategory>('all')
  const [featuredIndex, setFeaturedIndex] = useState(0)
  const [autoplay, setAutoplay] = useState(true)

  const {
    data: categoryGiveaways = [],
    isPending,
    isFetching,
    hasMore,
    loadMore,
    isLoadingMore,
  } = useGiveawayCategory(category)
  const { data: featuredGiveaways = [], isPending: featuredPending } =
    useGiveawayCategory('featured')

  const setSelectedGiveaway = useOverlayStore((state) => state.setSelectedGiveaway)
  const openOverlay = useOverlayStore((state) => state.open)

  // Featured falls back to whatever the current tab is showing, as before.
  const carousel = featuredGiveaways.length > 0 ? featuredGiveaways : categoryGiveaways
  const featured = carousel.at(featuredIndex % Math.max(carousel.length, 1))

  const select = (giveaway: Giveaway) => {
    setSelectedGiveaway(giveaway)
    openOverlay('giveaway-details')
  }

  const stepFeatured = (delta: number) => {
    if (carousel.length === 0) return
    setAutoplay(false)
    setFeaturedIndex((index) => (index + delta + carousel.length) % carousel.length)
  }

  // Advance on its own until the reader takes over, then leave it alone.
  useEffect(() => {
    if (!autoplay || carousel.length < 2) return
    const id = window.setInterval(
      () => setFeaturedIndex((index) => (index + 1) % carousel.length),
      AUTOPLAY_MS,
    )
    return () => window.clearInterval(id)
  }, [autoplay, carousel.length])

  const featuredStyle = {
    '--featured-art': featured?.display_cover.src
      ? `url(${JSON.stringify(featured.display_cover.src)})`
      : 'none',
  } as CSSProperties

  return (
    <div className="home">
      <section
        className="featured-section"
        style={featuredStyle}
        aria-label="Featured content"
        onMouseEnter={() => setAutoplay(false)}
      >
        <button
          type="button"
          className="featured-select left"
          aria-label="Previous featured giveaway"
          onClick={() => stepFeatured(-1)}
          disabled={carousel.length < 2}
        >
          <img className="featured-select-icon" src="/icons/left.png" alt="" />
        </button>

        <div className="content">
          <div className="heading">Featured Content</div>

          {featured ? (
            <FeaturedCard
              key={featured.id ?? featuredIndex}
              giveaway={featured}
              onSelect={select}
            />
          ) : featuredPending || isPending ? (
            <FeaturedSkeleton />
          ) : (
            <div className="empty-state">
              <div className="empty-state-title">Nothing featured right now</div>
              <div className="empty-state-body">
                Featured giveaways appear here as soon as one is running.
              </div>
            </div>
          )}

          {carousel.length > 1 ? (
            <div className="featured-selector">
              {carousel.map((entry, index) => (
                <button
                  type="button"
                  key={entry.id ?? index}
                  aria-label={`Show featured giveaway ${index + 1}`}
                  aria-current={featuredIndex === index}
                  className={cn('featured-selector-box', featuredIndex === index && 'active')}
                  onClick={() => {
                    setAutoplay(false)
                    setFeaturedIndex(index)
                  }}
                />
              ))}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          className="featured-select right"
          aria-label="Next featured giveaway"
          onClick={() => stepFeatured(1)}
          disabled={carousel.length < 2}
        >
          <img className="featured-select-icon" src="/icons/right.png" alt="" />
        </button>
      </section>

      <section className="giveaways-section">
        <div className="content">
          <div className="giveaways-selector" role="tablist" aria-label="Giveaway categories">
            {GIVEAWAY_CATEGORIES.map((entry) => (
              <button
                type="button"
                key={entry}
                role="tab"
                aria-selected={category === entry}
                className={cn('giveaways-selector-box', category === entry && 'active')}
                onClick={() => setCategory(entry)}
              >
                <div className="giveaways-selector-box-icon">
                  <img src={CATEGORY_ICONS[entry]} alt="" />
                </div>
                <div className="giveaways-selector-box-name">{CATEGORY_LABELS[entry]}</div>
              </button>
            ))}
          </div>

          <div className="giveaways-content-section">
            <div className="giveaways-content">
              {/* Switching tabs keeps the previous results on screen and dims
                  them, rather than emptying the grid and reflowing the page. */}
              <GiveawayFeed
                giveaways={categoryGiveaways}
                isPending={isPending}
                // The next page is a fetch, so it must not dim the grid the
                // reader is looking at.
                isRefetching={isFetching && !isPending && !isLoadingMore}
                onSelect={select}
                hasMore={hasMore}
                loadMore={loadMore}
                isLoadingMore={isLoadingMore}
                emptyTitle="Nothing here yet"
                emptyBody={`No ${CATEGORY_LABELS[category].toLowerCase()} giveaways are running. Try another category.`}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

function FeaturedCard({
  giveaway,
  onSelect,
}: {
  giveaway: Giveaway
  onSelect: (giveaway: Giveaway) => void
}) {
  useTicker(1000, !giveaway.has_ended)
  const remaining = giveaway.time_until_finish

  const units = [
    { key: 'd', value: remaining.days, label: 'd' },
    { key: 'h', value: remaining.hours, label: 'h' },
    { key: 'm', value: remaining.minutes, label: 'm' },
    { key: 's', value: remaining.seconds, label: 's' },
  ]

  return (
    <button
      type="button"
      className="featured-box rise"
      onClick={() => onSelect(giveaway)}
      title={giveaway.name ?? undefined}
    >
      <div className="featured-art" style={backgroundImage(giveaway.display_cover.src)} />

      <div className="featured-content">
        <div className="featured-header">
          <div className="featured-details">
            <span className="featured-title">{giveaway.name}</span>
            <span className="featured-subtitle">
              event by
              <span className="featured-username">&nbsp;{giveaway.owner?.display_name}</span>
            </span>
          </div>
          <Avatar
            src={giveaway.owner?.display_profile_picture.src}
            className="featured-avatar filled"
            label={`${giveaway.owner?.display_name ?? 'Owner'}'s avatar`}
          />
        </div>

        {giveaway.description ? (
          <div className="featured-description">{giveaway.description}</div>
        ) : null}

        <div className="featured-countdown">
          {units
            // Days is hidden once it hits zero, as in the original.
            .filter((unit) => unit.key !== 'd' || unit.value > 0)
            .map((unit) => (
              <div className="featured-countdown-section" key={unit.key}>
                <div className="featured-countdown-box">
                  <div className="featured-countdown-digit">{unit.value}</div>
                  <div className="featured-countdown-name">{unit.label}</div>
                </div>
              </div>
            ))}
        </div>

        <div className="featured-footer">
          <div className="featured-entries-section">
            <div className="featured-entries">
              {giveaway.shows_entry_count ? (
                <div className="giveaway-entries-box">
                  <div className="giveaway-entries-name">Entries</div>
                  <div className="giveaway-entries-digit">
                    {giveaway.display_total_entries.toLocaleString()}
                  </div>
                </div>
              ) : null}
              <div className="giveaway-entries-box">
                <div className="giveaway-entries-name">Your Entries</div>
                <div className="giveaway-entries-digit">
                  {giveaway.display_my_entries.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
          <ValueBadge gameInfo={giveaway.game_info} className="featured-price" />
        </div>
      </div>
    </button>
  )
}

/**
 * The featured card's own markup with the data greyed out, so it holds the
 * card's exact shape: title, owner and avatar, description, countdown, entry
 * boxes and the price badge. Fixed labels (d/h/m, "Entries") stay as text.
 */
function FeaturedSkeleton() {
  return (
    <div className="featured-box is-skeleton" aria-hidden="true">
      <div className="featured-art skeleton" />

      <div className="featured-content">
        <div className="featured-header">
          <div className="featured-details">
            <span className="featured-title">
              <SkeletonLine width="94%" />
              <SkeletonLine width="58%" />
            </span>
            <span className="featured-subtitle">
              <SkeletonLine width="52%" />
            </span>
          </div>
          <div className="featured-avatar skeleton" />
        </div>

        <div className="featured-description">
          <SkeletonLine />
          <SkeletonLine />
          <SkeletonLine width="62%" />
        </div>

        <div className="featured-countdown">
          {['d', 'h', 'm', 's'].map((unit) => (
            <div className="featured-countdown-section" key={unit}>
              <div className="featured-countdown-box">
                <div className="featured-countdown-digit">
                  <SkeletonLine width="1.1em" />
                </div>
                <div className="featured-countdown-name">{unit}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="featured-footer">
          <div className="featured-entries-section">
            <div className="featured-entries">
              {['Entries', 'Your Entries'].map((label) => (
                <div className="giveaway-entries-box" key={label}>
                  <div className="giveaway-entries-name">{label}</div>
                  <div className="giveaway-entries-digit">
                    <SkeletonLine width="2.4em" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <ValueBadgeSkeleton className="featured-price" />
        </div>
      </div>
    </div>
  )
}
